import type { ChartModel, ChartSeries, PlotAnalysisSource } from './types';

export type AnalysisDataPoint = [number, number | null];

export interface AnalysisSeries {
  id: string;
  plotId: string;
  name: string;
  label: string;
  unit: string;
  domainKey: string;
  xAxisType: 'datetime' | 'linear' | 'category';
  xAxisTitle?: string;
  sampleIntervalMs?: number;
  samplesPerCycle?: number;
  triggerSample?: number;
  data: AnalysisDataPoint[];
}

export interface AnalysisSummary {
  count: number;
  minimum: number;
  maximum: number;
  mean: number;
  median: number;
  standardDeviation: number;
  percentile05: number;
  percentile95: number;
  trendPerX: number | null;
  trendUnit: string;
}

export interface CorrelationResult {
  value: number | null;
  matchedSamples: number;
}

interface AlignedPoint {
  x: number;
  left: number;
  right: number;
  gapBefore: boolean;
}

export const ANALYSIS_COLOURS = ['#2f7ed8', '#c44e52', '#55a868', '#8172b2', '#dd8452', '#17a2a4'];
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

function measurementPoint(point: unknown): AnalysisDataPoint | null {
  if (Array.isArray(point) && finite(point[0])) return [point[0], finite(point[1]) ? point[1] : null];
  if (point && typeof point === 'object') {
    const candidate = point as { x?: unknown; y?: unknown };
    if (finite(candidate.x)) return [candidate.x, finite(candidate.y) ? candidate.y : null];
  }
  return null;
}

function numericData(series: AnalysisSeries): Array<[number, number]> {
  return series.data.flatMap(([x, value]) => value == null ? [] : [[x, value] as [number, number]]);
}

function domainKey(model: ChartModel): string {
  if (model.xAxisType === 'datetime') return 'datetime';
  return `${model.xAxisType ?? 'linear'}:${model.xAxisTitle ?? ''}`;
}

/** Return the visible measurement lines that can be compared numerically. */
export function analysisSeriesForSource(source: PlotAnalysisSource, panelLabel: string): AnalysisSeries[] {
  if (source.model.chartType === 'heatmap') return [];
  const result: AnalysisSeries[] = [];
  for (const [seriesIndex, series] of source.model.series.entries()) {
    if (
      series.visible === false
      || series.enableMouseTracking === false
      || series.yAxis === 1
      || series.type === 'arearange'
      || series.type === 'heatmap'
      || series.type === 'scatter'
    ) continue;

    const points = new Map<number, number | null>();
    for (const [pointIndex, item] of series.data.entries()) {
      const point = finite(item) || item == null
        ? [
            (series.pointStart ?? 0) + pointIndex * (series.pointInterval ?? 1),
            finite(item) ? item : null
          ] as AnalysisDataPoint
        : measurementPoint(item);
      if (point) points.set(point[0], point[1]);
    }
    const data = Array.from(points, ([x, y]) => [x, y] as AnalysisDataPoint)
      .sort(([left], [right]) => left - right);
    if (!data.some(([, value]) => value != null)) continue;
    if (series.linkedTo === ':previous' && result.length > 0) {
      const previous = result[result.length - 1];
      const previousEnd = [...previous.data].reverse().find(([, value]) => value != null)?.[0];
      const nextStart = data.find(([, value]) => value != null)?.[0];
      const separator: AnalysisDataPoint[] = previousEnd != null && nextStart != null
        ? [[(previousEnd + nextStart) / 2, null]]
        : [];
      previous.data = Array.from(new Map([...previous.data, ...separator, ...data]).entries())
        .map(([x, value]) => [x, value] as AnalysisDataPoint)
        .sort(([left], [right]) => left - right);
      continue;
    }
    const seriesUnit = typeof series.tooltip?.valueSuffix === 'string'
      ? series.tooltip.valueSuffix
      : source.model.valueSuffix ?? '';
    result.push({
      id: `${source.plotId}:${seriesIndex}`,
      plotId: source.plotId,
      name: series.name,
      label: `${panelLabel} - ${series.name}`,
      unit: seriesUnit,
      domainKey: domainKey(source.model),
      xAxisType: source.model.xAxisType ?? 'linear',
      xAxisTitle: source.model.xAxisTitle,
      sampleIntervalMs: source.model.waveformMetadata?.sampleIntervalMs,
      samplesPerCycle: source.model.waveformMetadata?.samplesPerCycle,
      triggerSample: source.model.waveformMetadata?.triggerSample,
      data
    });
  }
  return result;
}

function quantile(sorted: number[], probability: number): number {
  if (sorted.length === 1) return sorted[0];
  const position = (sorted.length - 1) * probability;
  const lower = Math.floor(position);
  const fraction = position - lower;
  return sorted[lower] + (sorted[Math.min(sorted.length - 1, lower + 1)] - sorted[lower]) * fraction;
}

function linearTrend(points: Array<[number, number]>): number | null {
  if (points.length < 2) return null;
  const meanX = points.reduce((sum, [x]) => sum + x, 0) / points.length;
  const meanY = points.reduce((sum, [, y]) => sum + y, 0) / points.length;
  let numerator = 0;
  let denominator = 0;
  for (const [x, y] of points) {
    numerator += (x - meanX) * (y - meanY);
    denominator += (x - meanX) ** 2;
  }
  return denominator > 0 ? numerator / denominator : null;
}

export function summarizeSeries(series: AnalysisSeries): AnalysisSummary {
  const points = numericData(series);
  const values = points.map(([, value]) => value).sort((left, right) => left - right);
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  const rawTrend = linearTrend(points);
  const datetime = series.xAxisType === 'datetime';
  return {
    count: values.length,
    minimum: values[0],
    maximum: values[values.length - 1],
    mean,
    median: quantile(values, 0.5),
    standardDeviation: Math.sqrt(variance),
    percentile05: quantile(values, 0.05),
    percentile95: quantile(values, 0.95),
    trendPerX: rawTrend == null ? null : rawTrend * (datetime ? 86_400_000 : 1),
    trendUnit: datetime ? '/day' : '/x unit'
  };
}

function medianPositiveStep(points: AnalysisDataPoint[]): number | null {
  const steps: number[] = [];
  for (let index = 1; index < points.length; index += 1) {
    if (points[index][1] == null || points[index - 1][1] == null) continue;
    const step = points[index][0] - points[index - 1][0];
    if (step > 0 && Number.isFinite(step)) steps.push(step);
  }
  if (steps.length === 0) return null;
  steps.sort((left, right) => left - right);
  return quantile(steps, 0.5);
}

function interpolatedValue(points: AnalysisDataPoint[], x: number, maximumGap: number): number | null {
  let low = 0;
  let high = points.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (points[middle][0] < x) low = middle + 1;
    else high = middle;
  }
  if (low < points.length && points[low][0] === x) return points[low][1];
  if (low === 0 || low >= points.length) return null;
  const before = points[low - 1];
  const after = points[low];
  const gap = after[0] - before[0];
  if (!(gap > 0) || gap > maximumGap || before[1] == null || after[1] == null) return null;
  return before[1] + (after[1] - before[1]) * (x - before[0]) / gap;
}

function hasMissingBetween(points: AnalysisDataPoint[], after: number, before: number): boolean {
  let low = 0;
  let high = points.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (points[middle][0] <= after) low = middle + 1;
    else high = middle;
  }
  for (let index = low; index < points.length && points[index][0] < before; index += 1) {
    if (points[index][1] == null) return true;
  }
  return false;
}

function alignPair(left: AnalysisSeries, right: AnalysisSeries): AlignedPoint[] {
  if (left.domainKey !== right.domainKey) return [];
  const referenceIsLeft = numericData(left).length <= numericData(right).length;
  const reference = referenceIsLeft ? left.data : right.data;
  const candidate = referenceIsLeft ? right.data : left.data;
  const typicalStep = medianPositiveStep(candidate);
  const maximumGap = typicalStep == null ? 0 : typicalStep * 3;
  const result: AlignedPoint[] = [];
  let skippedSincePrevious = false;
  let previousX: number | null = null;

  for (const [x, referenceValue] of reference) {
    if (referenceValue == null) {
      skippedSincePrevious = true;
      continue;
    }
    const candidateValue = interpolatedValue(candidate, x, maximumGap);
    if (candidateValue == null) {
      skippedSincePrevious = true;
      continue;
    }
    const gapBefore = previousX != null && (
      skippedSincePrevious
      || hasMissingBetween(reference, previousX, x)
      || hasMissingBetween(candidate, previousX, x)
    );
    result.push({
      x,
      left: referenceIsLeft ? referenceValue : candidateValue,
      right: referenceIsLeft ? candidateValue : referenceValue,
      gapBefore
    });
    previousX = x;
    skippedSincePrevious = false;
  }
  return result;
}

function pearson(points: Array<{ left: number; right: number }>): number | null {
  if (points.length < 3) return null;
  const meanLeft = points.reduce((sum, point) => sum + point.left, 0) / points.length;
  const meanRight = points.reduce((sum, point) => sum + point.right, 0) / points.length;
  let covariance = 0;
  let varianceLeft = 0;
  let varianceRight = 0;
  for (const point of points) {
    const left = point.left - meanLeft;
    const right = point.right - meanRight;
    covariance += left * right;
    varianceLeft += left * left;
    varianceRight += right * right;
  }
  const denominator = Math.sqrt(varianceLeft * varianceRight);
  if (!(denominator > 0)) return null;
  return Math.max(-1, Math.min(1, covariance / denominator));
}

function alignedSegments(points: AlignedPoint[]): AlignedPoint[][] {
  if (points.length === 0) return [];
  const steps = points.slice(1)
    .flatMap((point, index) => point.gapBefore ? [] : [point.x - points[index].x])
    .filter((step) => step > 0)
    .sort((left, right) => left - right);
  const typicalStep = steps.length ? quantile(steps, 0.5) : null;
  const maximumGap = typicalStep == null ? Number.POSITIVE_INFINITY : typicalStep * 3;
  const segments: AlignedPoint[][] = [];
  let current: AlignedPoint[] = [];
  for (let index = 0; index < points.length; index += 1) {
    if (current.length && (points[index].gapBefore || points[index].x - points[index - 1].x > maximumGap)) {
      segments.push(current);
      current = [];
    }
    current.push(points[index]);
  }
  if (current.length) segments.push(current);
  return segments;
}

function appendSegment(
  result: AnalysisDataPoint[],
  segment: Array<[number, number]>
): void {
  if (segment.length === 0) return;
  const previous = result.length ? result[result.length - 1] : undefined;
  if (previous) result.push([(previous[0] + segment[0][0]) / 2, null]);
  result.push(...segment);
}

export function correlationBetween(left: AnalysisSeries, right: AnalysisSeries): CorrelationResult {
  const aligned = alignPair(left, right);
  return { value: pearson(aligned), matchedSamples: aligned.length };
}

export function rollingCorrelationData(
  left: AnalysisSeries,
  right: AnalysisSeries,
  requestedWindow: number
): AnalysisDataPoint[] {
  const windowSize = Math.max(3, Math.floor(requestedWindow));
  const result: AnalysisDataPoint[] = [];
  for (const segment of alignedSegments(alignPair(left, right))) {
    const output: Array<[number, number]> = [];
    let sumLeft = 0;
    let sumRight = 0;
    let sumLeftSquared = 0;
    let sumRightSquared = 0;
    let sumProducts = 0;
    for (let end = 0; end < segment.length; end += 1) {
      const point = segment[end];
      sumLeft += point.left;
      sumRight += point.right;
      sumLeftSquared += point.left * point.left;
      sumRightSquared += point.right * point.right;
      sumProducts += point.left * point.right;
      if (end >= windowSize) {
        const expired = segment[end - windowSize];
        sumLeft -= expired.left;
        sumRight -= expired.right;
        sumLeftSquared -= expired.left * expired.left;
        sumRightSquared -= expired.right * expired.right;
        sumProducts -= expired.left * expired.right;
      }
      if (end < windowSize - 1) continue;
      const covariance = sumProducts - sumLeft * sumRight / windowSize;
      const leftVariance = Math.max(0, sumLeftSquared - sumLeft * sumLeft / windowSize);
      const rightVariance = Math.max(0, sumRightSquared - sumRight * sumRight / windowSize);
      const denominator = Math.sqrt(leftVariance * rightVariance);
      if (denominator > 0) output.push([point.x, Math.max(-1, Math.min(1, covariance / denominator))]);
    }
    appendSegment(result, output);
  }
  return result;
}

function differenceData(baseline: AnalysisSeries, comparison: AnalysisSeries): AnalysisDataPoint[] {
  const result: AnalysisDataPoint[] = [];
  for (const segment of alignedSegments(alignPair(baseline, comparison))) {
    appendSegment(result, segment.map((point) => [point.x, point.right - point.left]));
  }
  return result;
}

function baseModel(selected: AnalysisSeries[]): Pick<ChartModel, 'xAxisType' | 'xAxisTitle'> {
  return {
    xAxisType: selected[0]?.xAxisType ?? 'datetime',
    xAxisTitle: selected[0]?.xAxisTitle
  };
}

function comparisonSeries(selected: AnalysisSeries[], transform: (series: AnalysisSeries) => AnalysisDataPoint[]): ChartSeries[] {
  return selected.map((series, index) => ({
    name: series.label,
    color: ANALYSIS_COLOURS[index % ANALYSIS_COLOURS.length],
    data: transform(series),
    lineWidth: 1.5,
    marker: { enabled: false }
  }));
}

export function rollingCorrelationModel(selected: AnalysisSeries[], windowSize: number): ChartModel {
  const series: ChartSeries[] = [];
  for (let left = 0; left < selected.length; left += 1) {
    for (let right = left + 1; right < selected.length; right += 1) {
      series.push({
        name: `${selected[left].label} vs ${selected[right].label}`,
        color: ANALYSIS_COLOURS[series.length % ANALYSIS_COLOURS.length],
        data: rollingCorrelationData(selected[left], selected[right], windowSize),
        lineWidth: 1.5,
        marker: { enabled: false }
      });
    }
  }
  return {
    title: 'Rolling correlation over time',
    ...baseModel(selected),
    yAxisTitle: 'Pearson correlation (r)',
    yAxisOptions: { min: -1, max: 1, tickPositions: [-1, -0.5, 0, 0.5, 1] },
    legendEnabled: series.length > 1,
    series,
    emptyMessage: 'There are not enough overlapping samples for this correlation window.'
  };
}

export function normalizedComparisonModel(selected: AnalysisSeries[]): ChartModel {
  const series = comparisonSeries(selected, (item) => {
    const summary = summarizeSeries(item);
    const scale = summary.standardDeviation;
    return item.data.map(([x, value]) => [x, value == null ? null : scale > 0 ? (value - summary.mean) / scale : 0]);
  });
  return {
    title: 'Normalized comparison',
    ...baseModel(selected),
    yAxisTitle: 'Standard deviations from mean',
    valueSuffix: 'σ',
    legendEnabled: true,
    series,
    emptyMessage: 'No plotted samples are available to compare.'
  };
}

export function rawComparisonModel(selected: AnalysisSeries[]): ChartModel {
  const units = Array.from(new Set(selected.map((series) => series.unit)));
  const primaryUnit = units[0] ?? '';
  const secondaryUnit = units[1];
  return {
    title: 'Original value comparison',
    ...baseModel(selected),
    yAxisTitle: primaryUnit ? `Value (${primaryUnit})` : 'Value',
    valueSuffix: primaryUnit || undefined,
    secondaryYAxisTitle: secondaryUnit == null
      ? undefined
      : secondaryUnit ? `Value (${secondaryUnit})` : 'Value',
    legendEnabled: true,
    series: selected.map((series, index) => ({
      name: series.label,
      color: ANALYSIS_COLOURS[index % ANALYSIS_COLOURS.length],
      data: series.data,
      yAxis: secondaryUnit != null && series.unit === secondaryUnit ? 1 : 0,
      lineWidth: 1.5,
      marker: { enabled: false },
      tooltip: { valueSuffix: series.unit }
    })),
    emptyMessage: 'No plotted samples are available to compare.'
  };
}

export function differenceFromBaselineModel(selected: AnalysisSeries[]): ChartModel {
  const baseline = selected[0];
  const unit = baseline?.unit ?? '';
  const comparisons = selected.slice(1);
  return {
    title: baseline ? `Difference from ${baseline.label}` : 'Difference from baseline',
    ...baseModel(selected),
    yAxisTitle: unit ? `Difference (${unit})` : 'Difference',
    valueSuffix: unit || undefined,
    legendEnabled: true,
    series: comparisons.map((series, index) => ({
      name: `${series.label} minus ${baseline.label}`,
      color: ANALYSIS_COLOURS[(index + 1) % ANALYSIS_COLOURS.length],
      data: differenceData(baseline, series),
      lineWidth: 1.5,
      marker: { enabled: false }
    })),
    emptyMessage: 'There are not enough overlapping samples to calculate a difference.'
  };
}
